import { useState } from 'react';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Cookie, Settings } from 'lucide-react';

export function CookieConsent() {
  const { 
    showBanner, 
    preferences, 
    acceptAll, 
    rejectAll, 
    updatePreferences,
    setShowBanner 
  } = useCookieConsent();
  
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [tempPreferences, setTempPreferences] = useState(preferences);

  if (!showBanner) return null;

  const handleSaveCustom = () => {
    updatePreferences(tempPreferences);
    setIsCustomizing(false);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-in slide-in-from-bottom duration-300">
      <Card className="mx-auto max-w-4xl border-border bg-card/95 backdrop-blur-sm shadow-lg">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-1">
              <Cookie className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-2">
                  We value your privacy
                </h2>
                <p className="text-sm text-muted-foreground">
                  We use cookies to enhance your browsing experience, provide personalized content, and analyze our traffic. 
                  By clicking "Accept All", you consent to our use of cookies.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={acceptAll}
                  className="flex-1 sm:flex-none"
                >
                  Accept All
                </Button>
                <Button 
                  onClick={rejectAll}
                  variant="outline"
                  className="flex-1 sm:flex-none"
                >
                  Reject All
                </Button>
                
                <Sheet open={isCustomizing} onOpenChange={setIsCustomizing}>
                  <SheetTrigger asChild>
                    <Button 
                      variant="outline"
                      className="flex-1 sm:flex-none"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Customize
                    </Button>
                  </SheetTrigger>
                  <SheetContent>
                    <SheetHeader>
                      <SheetTitle>Cookie Preferences</SheetTitle>
                      <SheetDescription>
                        Choose which cookies you want to accept. Necessary cookies are required for the site to function.
                      </SheetDescription>
                    </SheetHeader>
                    
                    <div className="space-y-6 mt-6">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <Label className="text-base font-medium">Necessary Cookies</Label>
                            <p className="text-sm text-muted-foreground">
                              Required for the website to function properly. Cannot be disabled.
                            </p>
                          </div>
                          <Switch 
                            checked={true} 
                            disabled 
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <Label htmlFor="functional" className="text-base font-medium">Functional Cookies</Label>
                            <p className="text-sm text-muted-foreground">
                              Enable personalized features and remember your preferences.
                            </p>
                          </div>
                          <Switch 
                            id="functional"
                            checked={tempPreferences.functional}
                            onCheckedChange={(checked) => 
                              setTempPreferences(prev => ({ ...prev, functional: checked }))
                            }
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <Label htmlFor="analytics" className="text-base font-medium">Analytics Cookies</Label>
                            <p className="text-sm text-muted-foreground">
                              Help us understand how visitors interact with our website.
                            </p>
                          </div>
                          <Switch 
                            id="analytics"
                            checked={tempPreferences.analytics}
                            onCheckedChange={(checked) => 
                              setTempPreferences(prev => ({ ...prev, analytics: checked }))
                            }
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <Label htmlFor="marketing" className="text-base font-medium">Marketing Cookies</Label>
                            <p className="text-sm text-muted-foreground">
                              Used to deliver personalized advertisements relevant to you.
                            </p>
                          </div>
                          <Switch 
                            id="marketing"
                            checked={tempPreferences.marketing}
                            onCheckedChange={(checked) => 
                              setTempPreferences(prev => ({ ...prev, marketing: checked }))
                            }
                          />
                        </div>
                      </div>

                      <div className="flex gap-3 pt-4">
                        <Button onClick={handleSaveCustom} className="flex-1">
                          Save Preferences
                        </Button>
                        <Button 
                          onClick={() => setIsCustomizing(false)} 
                          variant="outline"
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
